import { supabase } from "@/lib/supabase";
import type {
  DailyActivityRow,
  PeriodMetricRow,
  RepRow,
  SyncRunRow,
  TeamPeriodMetricRow,
  TeamRow,
} from "@/raw/datasets/columns";

const PAGE_SIZE = 1000;

async function fetchAll<T>(
  fetchPage: (from: number, to: number) => Promise<{ data: T[] | null; error: Error | null }>,
): Promise<T[]> {
  const rows: T[] = [];
  let from = 0;

  while (true) {
    const to = from + PAGE_SIZE - 1;
    const { data, error } = await fetchPage(from, to);
    if (error) throw error;
    if (!data || data.length === 0) break;
    rows.push(...data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return rows;
}

export async function fetchSyncRuns(): Promise<SyncRunRow[]> {
  return fetchAll(async (from, to) => {
    const { data, error } = await supabase
      .from("sync_runs")
      .select(
        "id, source, trigger_type, job_kind, started_at, finished_at, status, rows_written, window_pulled, error_detail",
      )
      .order("started_at", { ascending: false })
      .range(from, to);

    return { data: data as SyncRunRow[] | null, error: error as Error | null };
  });
}

export async function fetchDailyActivities(): Promise<DailyActivityRow[]> {
  return fetchAll(async (from, to) => {
    const { data, error } = await supabase
      .from("daily_activities")
      .select(
        `
        id,
        rep_id,
        activity_date,
        total_calls,
        connected_calls,
        meetings_held,
        sequences,
        sync_run_id,
        updated_at,
        reps (
          full_name,
          team_id,
          teams (
            name,
            region
          )
        )
      `,
      )
      .order("activity_date", { ascending: false })
      .range(from, to);

    if (error) return { data: null, error: error as Error };

    const mapped = (data ?? []).map((row) => {
      const rep = row.reps as {
        full_name: string;
        team_id: number;
        teams: { name: string; region: string } | null;
      } | null;
      return {
        id: row.id as number,
        rep_id: row.rep_id as number,
        team_id: rep?.team_id ?? 0,
        activity_date: row.activity_date as string,
        rep_name: rep?.full_name ?? "—",
        team_name: rep?.teams?.name ?? "—",
        region: rep?.teams?.region ?? "—",
        total_calls: row.total_calls,
        connected_calls: row.connected_calls,
        meetings_held: row.meetings_held,
        sequences: row.sequences,
        sync_run_id: row.sync_run_id,
        updated_at: row.updated_at,
      } satisfies DailyActivityRow;
    });

    return { data: mapped, error: null };
  });
}

export async function fetchPeriodMetrics(): Promise<PeriodMetricRow[]> {
  return fetchAll(async (from, to) => {
    const { data, error } = await supabase
      .from("period_metrics")
      .select(
        `
        id,
        rep_id,
        period_type,
        period_start,
        sales_amount,
        sales_margin,
        opps_generated_count,
        opps_generated_value,
        currency,
        sync_run_id,
        updated_at,
        reps (
          full_name,
          team_id,
          teams (
            name,
            region
          )
        )
      `,
      )
      .order("period_start", { ascending: false })
      .range(from, to);

    if (error) return { data: null, error: error as Error };

    const mapped = (data ?? []).map((row) => {
      const rep = row.reps as {
        full_name: string;
        team_id: number;
        teams: { name: string; region: string } | null;
      } | null;
      return {
        id: row.id as number,
        rep_id: row.rep_id as number,
        team_id: rep?.team_id ?? 0,
        rep_name: rep?.full_name ?? "—",
        team_name: rep?.teams?.name ?? "—",
        region: rep?.teams?.region ?? "—",
        period_type: row.period_type as string,
        period_start: row.period_start as string,
        sales_amount: row.sales_amount,
        sales_margin: row.sales_margin,
        opps_generated_count: row.opps_generated_count,
        opps_generated_value: row.opps_generated_value,
        currency: row.currency,
        sync_run_id: row.sync_run_id,
        updated_at: row.updated_at,
      } satisfies PeriodMetricRow;
    });

    return { data: mapped, error: null };
  });
}

export async function fetchTeamPeriodMetrics(): Promise<TeamPeriodMetricRow[]> {
  return fetchAll(async (from, to) => {
    const { data, error } = await supabase
      .from("team_period_metrics")
      .select(
        `
        id,
        team_id,
        period_type,
        period_start,
        sales_amount,
        opps_generated_count,
        opps_generated_value,
        currency,
        sync_run_id,
        updated_at,
        teams (
          name,
          region
        )
      `,
      )
      .order("period_start", { ascending: false })
      .range(from, to);

    if (error) return { data: null, error: error as Error };

    const mapped = (data ?? []).map((row) => {
      const team = row.teams as { name: string; region: string } | null;
      return {
        id: row.id as number,
        team_id: row.team_id as number,
        team_name: team?.name ?? "—",
        region: team?.region ?? "—",
        period_type: row.period_type as string,
        period_start: row.period_start as string,
        sales_amount: row.sales_amount,
        opps_generated_count: row.opps_generated_count,
        opps_generated_value: row.opps_generated_value,
        currency: row.currency,
        sync_run_id: row.sync_run_id,
        updated_at: row.updated_at,
      } satisfies TeamPeriodMetricRow;
    });

    return { data: mapped, error: null };
  });
}

export async function fetchReps(): Promise<RepRow[]> {
  return fetchAll(async (from, to) => {
    const { data, error } = await supabase
      .from("reps")
      .select(
        `
        id,
        team_id,
        full_name,
        syncore_ref,
        hubspot_owner_id,
        active,
        start_date,
        teams (
          name,
          region
        )
      `,
      )
      .order("full_name", { ascending: true })
      .range(from, to);

    if (error) return { data: null, error: error as Error };

    const mapped = (data ?? []).map((row) => {
      const team = row.teams as { name: string; region: string } | null;
      return {
        id: row.id as number,
        team_id: row.team_id as number,
        full_name: row.full_name as string,
        team_name: team?.name ?? "—",
        region: team?.region ?? "—",
        syncore_ref: row.syncore_ref,
        hubspot_owner_id: row.hubspot_owner_id,
        active: row.active as boolean,
        start_date: row.start_date,
      } satisfies RepRow;
    });

    return { data: mapped, error: null };
  });
}

export async function fetchTeams(): Promise<TeamRow[]> {
  return fetchAll(async (from, to) => {
    const { data, error } = await supabase
      .from("teams")
      .select("id, name, department, region, syncore_ref, active")
      .order("name", { ascending: true })
      .range(from, to);

    return { data: data as TeamRow[] | null, error: error as Error | null };
  });
}
